import {
  ArrowDown, ArrowUp, ChevronDown, ChevronUp, ExternalLink, MinusCircle, PlusCircle, Search, Trash2, Users, XCircle,
} from 'react-feather';
import { ChangeEvent, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import {
  ContactType, GetContactStatsQuery as GetContactStats, Locale, ParticipantFilters,
} from '__generated__/graphql';

import { ParticipantFieldFilters } from '../ParticipantFieldFiltersForm';
import { UrlState, parseHelpers } from '../../../common/useUrlState';
import { encodeUrl, isEmpty, toggleInList } from '../../../common/helpers';
import EventPicker, { Event as SelectedEvent } from '../../Events/EventPicker';
import GetContactFiltersQuery from './GetContactFiltersQuery';
import GetContactStatsQuery from './GetContactStatsQuery';
import OrderFilters, {
  OrderFilterParams, OrderFilterType, useOrderFilterMapping,
} from '../Orders/OrderFilters';
import RegistrationFilters, {
  RegistrationFilterParams, RegistrationFilterType, useRegistrationFilterMapping,
} from '../Registrations/RegistrationFilters';
import UI from '../../../common/UI';
import config from '../../config';
import useLocale from '../../../common/useLocale';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';

export type ParticipantStats = GetContactStats['project']['participant_stats'];

export interface ParticipantFieldFilter {
  id: string;
  filters?: ParticipantFieldFilters;
}

export interface ContactFilterParams {
  event?: string | null;
  contact_type?: ContactType | null;
  search?: string | null;
  registration_filters?: RegistrationFilterParams;
  order_filters?: OrderFilterParams;
  locales?: Locale[];
  mail_opt_ins?: string[];
  received_messages?: string[];
  will_receive_messages?: string[];
  inclusions?: ContactFilterParams[];
  exclusions?: ContactFilterParams[];
}

export interface ContactFiltersProps {
  types?: (keyof ContactFilterParams)[];
  registrationFiltersTypes?: RegistrationFilterType[];
  orderFiltersTypes?: OrderFilterType[];
  filters: ContactFilterParams;
  onChange: (filters: ContactFilterParams) => unknown;
  onStatsChange?: (stats: ParticipantStats) => void;
  requireEvent?: boolean;
  showTotals?: boolean;
  showViewParticipantsButton?: boolean;
  showResetButton?: boolean;
  readOnly?: boolean;
}

const ContactFilters = ({
  types = [
    'search', 'event', 'contact_type', 'locales', 'mail_opt_ins',
    'registration_filters', 'order_filters', 'received_messages',
    'will_receive_messages',
  ],
  registrationFiltersTypes = [
    'tickets', 'products', 'team', 'statuses', 'participant_fields',
    'ticket_number_sequences', 'invitations', 'coupons', 'lotteries',
    'rejected_lotteries', 'registration_date',
  ],
  orderFiltersTypes = [
    'tickets', 'products', 'participant_fields', 'invitations', 'coupons',
    'lotteries', 'rejected_lotteries', 'assigned', 'payment_statuses',
  ],
  filters,
  onChange,
  onStatsChange,
  requireEvent = false,
  showTotals = true,
  showResetButton = true,
  showViewParticipantsButton = false,
  readOnly = false,
}: ContactFiltersProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const { formatNumber } = useLocale();

  const enabled = (type: keyof ContactFilterParams) => !types || types.includes(type);

  const searchRef = useRef<HTMLInputElement>();

  // Fetches the filters
  const { data: filtersData } = useQuery(
    GetContactFiltersQuery,
    {
      variables: {
        project_id: project.id,
        events: filters.event ? [filters.event] : [],
        will_receive_messages: filters.will_receive_messages,
        include_will_receive_messages: filters.will_receive_messages?.length > 0,
        include_received_messages: !!filters.event,
      },
      fetchPolicy: 'cache-first',
    },
  );

  const event = filtersData?.project.events.data.find(({ id }) => filters.event === id);

  const mailPermissions = filtersData?.project.mail_permissions.filter(
    (mailPermission) => !mailPermission.integration,
  ) || [];
  const receivedMessages = filtersData?.project.receivedMessages?.data || [];
  const willReceiveMessages = filtersData?.project.willReceiveMessages?.data || [];

  const { mapContactFiltersToServerInput } = useContactFilterMapping();

  // Fetches the stats
  const { data: statsData, loading: loadingStats } = useQuery(
    GetContactStatsQuery,
    {
      variables: {
        ...mapContactFiltersToServerInput(filters),
        project_id: project.id,
      },
      fetchPolicy: 'cache-and-network',
      skip: !showTotals,
      onCompleted: (data) => {
        onStatsChange?.(data.project.participant_stats);
      },
    },
  );

  const stats = statsData?.project.participant_stats;

  const getDefaultShowingFilters = (filters: ContactFilterParams) => ({
    contact_type: true, // Always show by default
    mail_opt_ins: filters.mail_opt_ins?.length > 0,
    locales: filters.locales?.length > 0,
    received_messages: filters.received_messages?.length > 0,
    will_receive_messages: filters.will_receive_messages?.length > 0,
    inclusions: filters.inclusions?.length > 0,
    exclusions: filters.exclusions?.length > 0,
  });

  // Indicates whether a filter category is collapsed or not
  const [showingFilters, setShowingFilters] = useState(() => getDefaultShowingFilters(filters));

  /**
   * Shows/hides a filter category.
   */
  const toggleShowingFilters = (key: keyof typeof showingFilters) => {
    setShowingFilters((showing) => ({
      ...showing,
      [key]: !showing[key],
    }));
  };

  const selectEvent = (event?: SelectedEvent) => {
    onChange({
      ...filters,
      event: event?.id,
      registration_filters: null,
      order_filters: null,
      received_messages: undefined, // Unset, only show messages if an event is selected
    });
  };

  /**
   * Handles changes to the search input by storing the value in the filters.
   */
  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...filters, search: event.target.value || null });
  };

  /**
   * Unsets the search term in the filters and empties the input.
   */
  const resetSearch = () => {
    onChange({ ...filters, search: null });

    searchRef.current.value = '';
  };

  /**
   * Toggles a filter in a list: if it's not in the list, it's added, otherwise it's removed.
   */
  const toggleListFilter = (
    name: 'locales' | 'mail_opt_ins' | 'received_messages' | 'will_receive_messages',
    id: string,
  ) => {
    onChange({
      ...filters,
      [name]: toggleInList([...filters[name] || []], id),
    });
  };

  const selectAllContacts = () => {
    onChange({
      ...filters,
      contact_type: null,
      registration_filters: null,
      order_filters: null,
    });
  };

  const selectParticipants = () => {
    onChange({
      ...filters,
      contact_type: ContactType.participant,
      registration_filters: {
        event: filters.event,
      },
      order_filters: null,
    });
  };

  const selectBuyers = () => {
    onChange({
      ...filters,
      contact_type: ContactType.buyer,
      registration_filters: null,
      order_filters: {
        event: filters.event,
      },
    });
  };

  const selectTeamCaptains = () => {
    onChange({
      ...filters,
      contact_type: ContactType.team_captain,
      registration_filters: null,
      order_filters: null,
    });
  };

  const selectEmailContacts = () => {
    onChange({
      ...filters,
      contact_type: ContactType.email_contact,
      registration_filters: null,
      order_filters: null,
    });
  };

  /**
   * Indicates whether a user has activated any filters.
   */
  const hasFilters = Object.values(filters).some((param) => !isEmpty(param));

  /**
   * Resets all filters, the UI state and search input
   */
  const resetFilters = (newFilters: ContactFilterParams = {}) => {
    onChange(newFilters);
    setShowingFilters(getDefaultShowingFilters(newFilters));

    if (searchRef.current) {
      searchRef.current.value = newFilters?.search || '';
    }
  };

  const handleSubfilterChange = (
    key: 'inclusions' | 'exclusions',
    index: number,
    newSubfilters: ContactFilterParams,
  ) => {
    onChange({
      ...filters,
      [key]: filters[key].map((subfilters, i) => (
        i === index ? newSubfilters : subfilters),
      ),
    });
  };

  const subfilterKeys = useRef({
    inclusions: (filters.inclusions || []).map((value, index) => index),
    exclusions: (filters.exclusions || []).map((value, index) => index),
  });

  const addSubfilter = (key: 'inclusions' | 'exclusions') => {
    onChange({
      ...filters,
      [key]: [
        ...filters[key] ?? [],
        { },
      ],
    });
    subfilterKeys.current[key].push(Math.max(0, ...subfilterKeys.current[key]) + 1);
  };

  const moveSubfilterUp = (key: 'inclusions' | 'exclusions', index: number) => {
    const newSubfilters = [...filters[key]];
    const current = newSubfilters[index];
    newSubfilters[index] = newSubfilters[index - 1];
    newSubfilters[index - 1] = current;

    onChange({
      ...filters,
      [key]: newSubfilters,
    });

    const domKey = subfilterKeys.current[key][index];
    subfilterKeys.current[key][index] = subfilterKeys.current[key][index - 1];
    subfilterKeys.current[key][index - 1] = domKey;
  };

  const moveSubfilterDown = (key: 'inclusions' | 'exclusions', index: number) => {
    const newSubfilters = [...filters[key]];
    const current = newSubfilters[index];
    newSubfilters[index] = newSubfilters[index + 1];
    newSubfilters[index + 1] = current;

    onChange({
      ...filters,
      [key]: newSubfilters,
    });

    const domKey = subfilterKeys.current[key][index];
    subfilterKeys.current[key][index] = subfilterKeys.current[key][index + 1];
    subfilterKeys.current[key][index + 1] = domKey;
  };

  const deleteSubfilter = (key: 'inclusions' | 'exclusions', index: number) => {
    onChange({
      ...filters,
      [key]: filters[key].filter((subfilters, i) => i !== index),
    });
  };

  return (
    <UI.FormGrid sc={{ gutter: 2 / 3 }}>
      <UI.Delimit delimiter={<UI.HR sc={{ dotted: true }} />}>
        {(enabled('search') || enabled('event')) && (
          <>
            {enabled('event') && (
              <UI.InputGroup>
                <EventPicker
                  eventId={filters.event}
                  onChange={selectEvent}
                  renderActive={!requireEvent ? (event) => (
                    <UI.Span sc={{ color: 'gray.800' }} style={{ fontWeight: '500' }}>
                      {event?.title || t('all_events')}
                    </UI.Span>
                  ) : undefined}
                  nullable={!requireEvent}
                  disabled={!enabled('event')}
                />
              </UI.InputGroup>
            )}

            {enabled('search') && (
              <UI.InputGroup>
                <UI.InputWrapper>
                  <UI.Span>
                    <UI.Icon>
                      <Search />
                    </UI.Icon>
                  </UI.Span>
                  <UI.DebouncedInput
                    type="text"
                    value={filters.search}
                    onChange={handleSearch}
                    placeholder={t('search_contacts')}
                    ref={searchRef}
                    sc={{ px: 5 }}
                    disabled={readOnly}
                  />
                  {filters.search && (
                    <UI.Span>
                      <UI.A onClick={() => resetSearch()} sc={{ color: 'gray.400' }}>
                        <UI.Icon>
                          <XCircle />
                        </UI.Icon>
                      </UI.A>
                    </UI.Span>
                  )}
                </UI.InputWrapper>
              </UI.InputGroup>
            )}
          </>
        )}

        {(event || !requireEvent) && enabled('contact_type') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('contact_type')}
              role="button"
              sc={{ mb: showingFilters.contact_type ? 2 : 0 }}
            >
              {t('contact_type')}
              {' '}
              {!showingFilters.contact_type && filters.contact_type && (
                <UI.Badge sc={{ ml: 1 }}>1</UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.contact_type && <ChevronUp />}
                {!showingFilters.contact_type && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.contact_type && (
              <>
                <UI.Radio
                  onChange={() => selectAllContacts()}
                  checked={!filters.contact_type}
                >
                  {t('all_contacts')}
                </UI.Radio>
                <UI.Radio
                  onChange={() => selectParticipants()}
                  checked={filters.contact_type === ContactType.participant}
                >
                  {t('participants')}
                </UI.Radio>
                <UI.Radio
                  onChange={() => selectBuyers()}
                  checked={filters.contact_type === ContactType.buyer}
                >
                  {t('buyers')}
                </UI.Radio>
                <UI.Radio
                  onChange={() => selectTeamCaptains()}
                  checked={filters.contact_type === ContactType.team_captain}
                >
                  {t('team_captains')}
                </UI.Radio>
                <UI.Radio
                  onChange={() => selectEmailContacts()}
                  checked={filters.contact_type === ContactType.email_contact}
                >
                  {t('email_contacts')}
                </UI.Radio>
              </>
            )}
          </UI.InputGroup>
        )}

        {filters.contact_type === ContactType.participant && enabled('registration_filters') && (
          <RegistrationFilters
            types={registrationFiltersTypes}
            filters={{
              ...filters.registration_filters,
              event: filters.event,
            }}
            onChange={(registrationFilters) => onChange({
              ...filters,
              registration_filters: {
                ...filters.registration_filters,
                ...registrationFilters,
              },
            })}
            showResetButton={false}
            showTotals={false}
          />
        )}

        {filters.contact_type === ContactType.buyer && enabled('order_filters') && (
          <OrderFilters
            types={orderFiltersTypes}
            filters={{
              ...filters.order_filters,
              event: filters.event,
            }}
            onChange={(orderFilters) => onChange({
              ...filters,
              order_filters: {
                ...filters.order_filters,
                ...orderFilters,
              },
            })}
            showResetButton={false}
            showTotals={false}
          />
        )}

        {enabled('mail_opt_ins') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('mail_opt_ins')}
              role="button"
              sc={{ mb: showingFilters.mail_opt_ins ? 2 : 0 }}
            >
              {t('mail_opt_ins')}
              {' '}
              {!showingFilters.mail_opt_ins && filters.mail_opt_ins?.length > 0 && (
                <UI.Badge sc={{ ml: 1 }}>{formatNumber(filters.mail_opt_ins.length)}</UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.mail_opt_ins && <ChevronUp />}
                {!showingFilters.mail_opt_ins && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.mail_opt_ins && (
              <>
                {mailPermissions.map((mailPermission) => (
                  <UI.Checkbox
                    onChange={() => toggleListFilter('mail_opt_ins', mailPermission.id)}
                    checked={!!filters.mail_opt_ins?.includes(mailPermission.id)}
                    disabled={readOnly}
                    key={mailPermission.id}
                  >
                    {mailPermission.title}
                  </UI.Checkbox>
                ))}
                {mailPermissions.length === 0 && (
                  <UI.Div sc={{ color: 'gray.500' }}>
                    {t('no_mail_permissions_added_yet')}
                    {' '}
                    <UI.Link to={`/${project.id}/communication/permissions`} target="_blank" rel="noopener noreferrer">
                      {t('manage')}
                    </UI.Link>
                  </UI.Div>
                )}
              </>
            )}
          </UI.InputGroup>
        )}

        {enabled('locales') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('locales')}
              role="button"
              sc={{ mb: showingFilters.locales ? 2 : 0 }}
            >
              {t('preferred_language')}
              {' '}
              {!showingFilters.locales && filters.locales?.length > 0 && (
                <UI.Badge sc={{ ml: 1 }}>
                  {formatNumber(filters.locales.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.locales && <ChevronUp />}
                {!showingFilters.locales && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.locales && config.locales.map((locale) => (
              <UI.Checkbox
                key={`locale_${locale}`}
                checked={!!filters.locales?.includes(locale)}
                onChange={() => toggleListFilter('locales', locale)}
                disabled={readOnly}
              >
                {t(`common:locales.${locale}`)}
              </UI.Checkbox>
            ))}
          </UI.InputGroup>
        )}

        {receivedMessages.length > 0 && enabled('received_messages') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('received_messages')}
              role="button"
              sc={{ mb: showingFilters.received_messages ? 2 : 0 }}
            >
              {t('received_message')}
              {' '}
              {!showingFilters.received_messages && filters.received_messages?.length > 0 && (
                <UI.Badge sc={{ ml: 1 }}>{filters.received_messages.length}</UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.received_messages && <ChevronUp />}
                {!showingFilters.received_messages && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.received_messages && receivedMessages.map((message) => (
              <UI.Checkbox
                checked={!!filters.received_messages?.includes(message.id)}
                onChange={() => toggleListFilter('received_messages', message.id)}
                disabled={readOnly}
                key={message.id}
              >
                {message.title}
              </UI.Checkbox>
            ))}
          </UI.InputGroup>
        )}

        {willReceiveMessages.length > 0 && enabled('will_receive_messages') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('will_receive_messages')}
              role="button"
              sc={{ mb: showingFilters.will_receive_messages ? 2 : 0 }}
            >
              {t('will_receive_message')}
              {' '}
              {!showingFilters.will_receive_messages && filters.will_receive_messages?.length > 0 && (
                <UI.Badge sc={{ ml: 1 }}>{filters.will_receive_messages.length}</UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.will_receive_messages && <ChevronUp />}
                {!showingFilters.will_receive_messages && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.will_receive_messages && willReceiveMessages.map((message) => (
              <UI.Checkbox
                checked={!!filters.will_receive_messages?.includes(message.id)}
                onChange={() => toggleListFilter('will_receive_messages', message.id)}
                disabled={readOnly}
              >
                {message.title}
              </UI.Checkbox>
            ))}
          </UI.InputGroup>
        )}

        {enabled('inclusions') && (
          <UI.Div>
            <CategoryHeading
              onClick={() => toggleShowingFilters('inclusions')}
              role="button"
              sc={{ mb: showingFilters.inclusions ? 2 : 0 }}
            >
              {t('selections')}
              {' '}
              {!showingFilters.inclusions && filters.inclusions?.length > 0 && (
                <UI.Badge sc={{ ml: 1 }}>
                  {formatNumber(filters.inclusions.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.inclusions && <ChevronUp />}
                {!showingFilters.inclusions && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.inclusions && (
              <UI.GridContainer sc={{ gutter: 2 / 3 }}>
                {filters.inclusions?.map((subfilters, index) => (
                  <UI.Card
                    sc={{ outline: 'gray.200', borderRadius: 'lg' }}
                    key={subfilterKeys.current.inclusions[index]}
                  >
                    <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <UI.Label sc={{ brand: 'primary.25', color: 'primary.700', fontWeight: 500, small: true }}>
                        <UI.Icon>
                          <MinusCircle />
                        </UI.Icon>
                        {' '}
                        {t('selection_n', { n: formatNumber(index + 1) })}
                      </UI.Label>
                      <UI.FlexContainer sc={{ spaceX: 0.5 }}>
                        {filters.inclusions.length > 1 && (
                          <>
                            <UI.Button
                              onClick={() => moveSubfilterUp('inclusions', index)}
                              sc={{ size: 'sm', blank: true }}
                              disabled={index === 0}
                              title={t('move_up_option', {
                                option: t('selection_n', { n: formatNumber(index + 1) }),
                              })}
                            >
                              <UI.Icon>
                                <ArrowUp />
                              </UI.Icon>
                            </UI.Button>
                            <UI.Button
                              onClick={() => moveSubfilterDown('inclusions', index)}
                              sc={{ size: 'sm', blank: true }}
                              disabled={index === filters.inclusions.length - 1}
                              title={t('move_down_option', {
                                option: t('selection_n', { n: formatNumber(index + 1) }),
                              })}
                            >
                              <UI.Icon>
                                <ArrowDown />
                              </UI.Icon>
                            </UI.Button>
                          </>
                        )}
                        <UI.Button
                          onClick={() => deleteSubfilter('inclusions', index)}
                          sc={{ size: 'sm', blank: true }}
                          title={t('delete_option', {
                            option: t('selection_n', { n: formatNumber(index + 1) }),
                          })}
                        >
                          <UI.Icon>
                            <Trash2 />
                          </UI.Icon>
                        </UI.Button>
                      </UI.FlexContainer>
                    </UI.FlexContainer>
                    <ContactFilters
                      filters={subfilters}
                      onChange={(newSubfilters) => handleSubfilterChange('inclusions', index, newSubfilters)}
                      types={['event', 'contact_type', 'registration_filters', 'order_filters', 'received_messages']}
                      registrationFiltersTypes={registrationFiltersTypes}
                      orderFiltersTypes={orderFiltersTypes}
                      requireEvent
                      showResetButton={false}
                      showTotals={false}
                    />
                  </UI.Card>
                ))}

                {(!filters.inclusions || filters.inclusions.length === 0) && (
                  <UI.Div sc={{ color: 'gray.600' }}>
                    {t('contact_inclusions_description')}
                  </UI.Div>
                )}

                <UI.Button
                  onClick={() => addSubfilter('inclusions')}
                  sc={{ brand: 'primary.50', borderRadius: 'lg' }}
                >
                  <UI.Icon>
                    <PlusCircle />
                  </UI.Icon>
                  {' '}
                  {t('select_contacts')}
                </UI.Button>
              </UI.GridContainer>
            )}
          </UI.Div>
        )}

        {enabled('exclusions') && (
          <UI.Div>
            <CategoryHeading
              onClick={() => toggleShowingFilters('exclusions')}
              role="button"
              sc={{ mb: showingFilters.exclusions ? 2 : 0 }}
            >
              {t('exclusions')}
              {' '}
              {!showingFilters.exclusions && filters.exclusions?.length > 0 && (
                <UI.Badge sc={{ ml: 1 }}>
                  {formatNumber(filters.exclusions.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.exclusions && <ChevronUp />}
                {!showingFilters.exclusions && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.exclusions && (
              <UI.GridContainer sc={{ gutter: 2 / 3 }}>
                {filters.exclusions?.map((subfilters, index) => (
                  <UI.Card
                    sc={{ outline: 'gray.200', borderRadius: 'lg' }}
                    key={subfilterKeys.current.exclusions[index]}
                  >
                    <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <UI.Label sc={{ brand: 'error.25', color: 'error.700', fontWeight: 500, small: true }}>
                        <UI.Icon>
                          <MinusCircle />
                        </UI.Icon>
                        {' '}
                        {t('exclusion_n', { n: formatNumber(index + 1) })}
                      </UI.Label>
                      <UI.FlexContainer sc={{ spaceX: 0.5 }}>
                        {filters.exclusions.length > 1 && (
                          <>
                            <UI.Button
                              onClick={() => moveSubfilterUp('exclusions', index)}
                              sc={{ size: 'sm', blank: true }}
                              disabled={index === 0}
                              title={t('move_up_option', {
                                option: t('exclusion_n', { n: formatNumber(index + 1) }),
                              })}
                            >
                              <UI.Icon>
                                <ArrowUp />
                              </UI.Icon>
                            </UI.Button>
                            <UI.Button
                              onClick={() => moveSubfilterDown('exclusions', index)}
                              sc={{ size: 'sm', blank: true }}
                              disabled={index === filters.exclusions.length - 1}
                              title={t('move_down_option', {
                                option: t('exclusion_n', { n: formatNumber(index + 1) }),
                              })}
                            >
                              <UI.Icon>
                                <ArrowDown />
                              </UI.Icon>
                            </UI.Button>
                          </>
                        )}
                        <UI.Button
                          onClick={() => deleteSubfilter('exclusions', index)}
                          sc={{ size: 'sm', blank: true }}
                          title={t('delete_option', {
                            option: t('exclusion_n', { n: formatNumber(index + 1) }),
                          })}
                        >
                          <UI.Icon>
                            <Trash2 />
                          </UI.Icon>
                        </UI.Button>
                      </UI.FlexContainer>
                    </UI.FlexContainer>
                    <ContactFilters
                      filters={subfilters}
                      onChange={(newSubfilters) => handleSubfilterChange('exclusions', index, newSubfilters)}
                      types={['event', 'contact_type', 'registration_filters', 'order_filters', 'received_messages']}
                      registrationFiltersTypes={registrationFiltersTypes}
                      orderFiltersTypes={orderFiltersTypes}
                      requireEvent
                      showResetButton={false}
                      showTotals={false}
                    />
                  </UI.Card>
                ))}

                {(!filters.exclusions || filters.exclusions.length === 0) && (
                  <UI.Div sc={{ color: 'gray.600' }}>
                    {t('contacts_exclusion_description')}
                  </UI.Div>
                )}

                <UI.Button
                  onClick={() => addSubfilter('exclusions')}
                  sc={{ brand: 'error.100', borderRadius: 'lg' }}
                >
                  <UI.Icon>
                    <MinusCircle />
                  </UI.Icon>
                  {' '}
                  {t('exclude_contacts')}
                </UI.Button>
              </UI.GridContainer>
            )}
          </UI.Div>
        )}

        {(showTotals || (showResetButton && enabled('event'))) && (
          <>
            {showTotals && (
              <UI.Card
                sc={{
                  spacing: 'xs',
                  outline: 'gray.200',
                  background: 'gray.50',
                  loading: loadingStats,
                  borderRadius: 'md',
                  small: true,
                }}
              >
                <UI.Delimit>
                  <UI.Span sc={{ color: 'gray.600', inline: true }} style={{ fontWeight: 500 }}>
                    <UI.Icon sc={{ mr: 1, mb: 0.25 }}>
                      {loadingStats ? <UI.Loader sc={{ size: 14 }} /> : <Users />}
                    </UI.Icon>
                    <Trans
                      i18nKey="n_contacts_in_selection"
                      values={{
                        count: stats?.participants || 0,
                        value: formatNumber(stats?.participants || 0),
                      }}
                    >
                      <UI.Strong />
                    </Trans>
                  </UI.Span>
                  {showViewParticipantsButton && (
                    <UI.A href={`/b/${project.id}/participants/contacts?${encodeUrl(filters)}`} target="_blank">
                      {t('view')}
                      {' '}
                      <UI.Icon sc={{ mb: 1 / 3 }}>
                        <ExternalLink />
                      </UI.Icon>
                    </UI.A>
                  )}
                </UI.Delimit>
              </UI.Card>
            )}

            {showResetButton && enabled('event') && hasFilters && (
              <UI.Div>
                <UI.Button onClick={() => resetFilters()}>
                  {t('reset_filters')}
                </UI.Button>
              </UI.Div>
            )}
          </>
        )}
      </UI.Delimit>
    </UI.FormGrid>
  );
};

const CategoryHeading = styled(UI.A)`
  ${({ theme }) => css`
    color: ${theme.colors.gray[600]};
    font-weight: 500;
    display: flex;
    align-items: center;
    line-height: 21px;

    > ${UI.Icon} {
      margin-bottom: 0;
    }
  `}
`;

export const useContactFilterMapping = () => {
  const {
    mapRegistrationFiltersToServerInput, mapServerOutputToRegistrationFilters, parseRegistrationFiltersUrlState,
  } = useRegistrationFilterMapping();

  const {
    mapOrderFiltersToServerInput, mapServerOutputToOrderFilters, parseOrderFiltersUrlState,
  } = useOrderFilterMapping();

  const mapContactFiltersToServerInput = (filters: ContactFilterParams): ParticipantFilters => ({
    events: filters.event ? [filters.event] : undefined,
    contact_types: filters.contact_type ? [filters.contact_type] : undefined,
    search: filters.search ?? undefined,
    locales: filters.locales ?? undefined,
    mail_opt_ins: filters.mail_opt_ins ?? undefined,
    received_messages: filters.received_messages ?? undefined,
    will_receive_messages: filters.will_receive_messages ?? undefined,

    // Subfilters
    registration_filters: filters.registration_filters
      ? mapRegistrationFiltersToServerInput(filters.registration_filters) : undefined,
    order_filters: filters.order_filters ? mapOrderFiltersToServerInput(filters.order_filters) : undefined,

    // Nested filters (strip empty subfilters)
    inclusions: filters.inclusions?.filter((subfilters) => !isEmpty(subfilters)).map(mapContactFiltersToServerInput),
    exclusions: filters.exclusions?.filter((subfilters) => !isEmpty(subfilters)).map(mapContactFiltersToServerInput),
  });

  const mapServerOutputToContactFilters = (filters: ParticipantFilters): ContactFilterParams => ({
    event: filters.events?.[0],
    contact_type: filters.contact_types?.[0],
    locales: filters.locales ?? undefined,
    mail_opt_ins: filters.mail_opt_ins ?? undefined,
    received_messages: filters.received_messages ?? undefined,
    will_receive_messages: filters.will_receive_messages ?? undefined,

    // Registration filters
    registration_filters: filters.registration_filters
      ? mapServerOutputToRegistrationFilters(filters.registration_filters) : undefined,

    // Order filters
    order_filters: filters.order_filters
      ? mapServerOutputToOrderFilters(filters.order_filters) : undefined,

    // Nested filters
    inclusions: filters.inclusions?.map(mapServerOutputToContactFilters),
    exclusions: filters.exclusions?.map(mapServerOutputToContactFilters),
  });

  const parseContactFiltersUrlState = (params: UrlState): ContactFilterParams => ({
    event: parseHelpers.string(params.event),
    contact_type: parseHelpers.enumeration(params.contact_type, ContactType),
    search: parseHelpers.string(params.search),
    locales: parseHelpers.array(params.locales, parseHelpers.string, null),
    mail_opt_ins: parseHelpers.array(params.mail_opt_ins, parseHelpers.string, null),
    received_messages: parseHelpers.array(params.received_messages, parseHelpers.string, null),
    will_receive_messages: parseHelpers.array(params.will_receive_messages, parseHelpers.string, null),
    registration_filters: parseHelpers.object(
      params.registration_filters,
      (value) => value && parseRegistrationFiltersUrlState(value),
      null,
    ),
    order_filters: parseHelpers.object(
      params.order_filters,
      (value) => value && parseOrderFiltersUrlState(value),
      null,
    ),
  });

  return {
    mapContactFiltersToServerInput,
    mapServerOutputToContactFilters,
    parseContactFiltersUrlState,
  };
};

export default ContactFilters;
